import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * Full-screen video popup that opens once per visitor and remembers
 * the dismissal in a cookie.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLVideoElement} [video]
 * @property {HTMLVideoElement} [mobileVideo]
 * @property {HTMLButtonElement} [soundButton]
 *
 * @extends {DialogComponent}
 */
class VideoPopup extends DialogComponent {
  /** @type {number|undefined} */
  #timeout = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose);

    if (Shopify.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect);
      document.addEventListener('shopify:block:deselect', this.#handleBlockDeselect);
      return;
    }

    if (this.#hasBeenDismissed) return;

    this.#timeout = setTimeout(() => this.open(), this.delay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    clearTimeout(this.#timeout);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleClose);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#handleBlockDeselect);
  }

  open() {
    this.showDialog();

    this.#activeVideo?.play().catch(() => {});
  }

  toggleSound() {
    const { soundButton } = this.refs;
    const video = this.#activeVideo;
    if (!video) return;

    const muted = !video.muted;
    for (const v of this.#videos) v.muted = muted;
    if (!muted) video.play().catch(() => {});

    soundButton?.setAttribute('aria-pressed', String(!video.muted));
    soundButton?.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Turn sound off');
  }

  get cookieName() {
    return this.dataset.cookieName;
  }

  get delay() {
    return Number(this.dataset.delay || 0) * 1000;
  }

  /** @returns {HTMLVideoElement[]} */
  get #videos() {
    const { video, mobileVideo } = this.refs;
    return [video, mobileVideo].filter((v) => v instanceof HTMLVideoElement);
  }

  /** The video visible at the current breakpoint. */
  get #activeVideo() {
    const { video, mobileVideo } = this.refs;
    if (video && mobileVideo) return isMobileBreakpoint() ? mobileVideo : video;
    return video ?? mobileVideo;
  }

  get #hasBeenDismissed() {
    return Boolean(this.cookieName) && document.cookie.includes(`${this.cookieName}=1`);
  }

  #handleClose = () => {
    for (const v of this.#videos) v.pause();

    if (Shopify.designMode || !this.cookieName) return;

    const days = Number(this.dataset.cookieDays || 365);
    document.cookie = `${this.cookieName}=1; max-age=${days * 24 * 60 * 60}; path=/; SameSite=Lax`;
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    if (event.target instanceof Node && (this.contains(event.target) || event.target.contains(this))) {
      this.open();
    }
  };

  /** @param {Event} event */
  #handleBlockDeselect = (event) => {
    if (event.target instanceof Node && (this.contains(event.target) || event.target.contains(this))) {
      this.closeDialog();
    }
  };
}

if (!customElements.get('video-popup')) {
  customElements.define('video-popup', VideoPopup);
}
